import React from 'react';

import { observer } from 'mobx-react';

import { reverse } from 'named-urls';

import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

import { IconButton } from '@mui/material';

import NavBarBase from 'ui/NavBar/NavBar';
import { HELP_CENTRE_URL, WHATS_NEW_URL } from 'ui/NavBar/constants';

import API from 'utils/api';
import Logger from 'utils/logger';
import { useStore } from 'utils/hooks/store';

import SessionUser from 'stores/Session/SessionUser';
import routes from 'core/routes';
import { env } from 'config';

import { colors } from 'theme/colors';

import { resolveDefaultSettingsPath } from 'containers/Dashboard/components/SideBar/utils';

const NavBar: React.FunctionComponent = () => {
  const {
    session,
    notification,
    ui: { setSubSideBarOpen, subSideBarOpen },
    routing,
  } = useStore();

  const onLogout = async (): Promise<void> => {
    try {
      const resp = await API.getFusionAuthApplicationData({ username: session.user?.email as string })();
      const { applicationClientId } = resp.data;

      await API.logout()();
      session.logout();
      document.body.style.backgroundColor = colors.common.white;

      const searchParams = new URLSearchParams({
        client_id: applicationClientId as string,
      });

      const redirectUrl = applicationClientId
        ? `${env.REACT_APP_FUSION_AUTH_URL}/oauth2/logout/?${searchParams.toString()}`
        : `${env.REACT_APP_WORKSPACE_URL}/login/`;

      window.location.assign(redirectUrl);
    } catch (e) {
      notification.enqueueErrorSnackbar('You cannot log out right now');
      Logger.error('Invalid logout API response', e);
    }
  };

  const redirectToUser = (): void => {
    routing.push(reverse(routes.dashboard.settings.users.details, { userId: session.user?.uuid as string }));
  };

  const openHelpCentre = (): void => {
    window.open(session.user?.helpCenterUrls.helpCenterUrl || HELP_CENTRE_URL);
  };

  const openWhatsNew = (): void => {
    window.open(WHATS_NEW_URL);
  };

  const openSettings = (): void => {
    if (session.user) {
      routing.push(resolveDefaultSettingsPath(session.user));
    }
    if (!subSideBarOpen) {
      setSubSideBarOpen();
    }
  };

  return (
    <NavBarBase
      onRedirectToUserDetails={redirectToUser}
      collapseSubSideBar={setSubSideBarOpen}
      isSubSideBarCollapsed={!subSideBarOpen}
      onLogout={onLogout}
      onOpenHelpCentre={openHelpCentre}
      onOpenWhatsNew={openWhatsNew}
      user={{
        ...(session.user as SessionUser),
        userId: session.user?.organisation.accountNumber || '',
        accountName: session.user?.organisation.name || '',
      }}
      helpPanel={
        <IconButton onClick={openSettings} aria-label="Settings">
          <SettingsOutlinedIcon sx={{ color: 'text.secondary' }} />
        </IconButton>
      }
    />
  );
};

export default observer(NavBar);
